/**
 * SiteReliabilityCard
 *
 * Full-width overview card for GET /admin-api/site/{siteId}/reliability: the site-level
 * hint once at the top, then every configured bay. Refreshes via useSiteReliability.
 */
import type { SuperBay } from '../../models/domainModels';
import { useSiteReliability } from '../../hooks/useSiteReliability';
import { overallTone } from '../../utils/reliabilityPresentation';
import { formatImpactTime } from '../../utils/latencyClassifier';
import { SiteReliabilitySummary } from './SiteReliabilitySummary';
import { ReliabilityBayGrid } from './ReliabilityBayGrid';
import './SiteReliability.css';

interface SiteReliabilityCardProps {
  readonly siteId: string;
  readonly superBays: readonly SuperBay[];
}

export function SiteReliabilityCard({ siteId, superBays }: SiteReliabilityCardProps): JSX.Element {
  const { data, isLoading, isNotFound, error } = useSiteReliability(siteId);
  const tone = data ? overallTone(data.site, data.bays) : 'neutral';

  return (
    <section
      className={`site-overview__card site-reliability site-reliability--${tone}`}
      aria-labelledby="site-reliability-title"
    >
      <div className="site-reliability__header">
        <h3 id="site-reliability-title" className="site-overview__card-title">
          Reliability
        </h3>
        {data && (
          <p className="site-reliability__evaluated">
            Evaluated {formatImpactTime(data.evaluatedAt)}
          </p>
        )}
      </div>
      {renderContent()}
    </section>
  );

  function renderContent(): JSX.Element {
    if (isLoading) {
      return <p className="site-reliability__message">Loading…</p>;
    }

    if (data) {
      return (
        <>
          {error && (
            <p className="site-reliability__stale" role="status">
              Couldn’t refresh ({error}). Showing the last successful result.
            </p>
          )}
          <SiteReliabilitySummary site={data.site} bays={data.bays} />
          <ReliabilityBayGrid
            bays={data.bays}
            site={data.site}
            superBays={superBays}
            evaluatedAt={data.evaluatedAt}
          />
        </>
      );
    }

    if (error) {
      return <p className="site-reliability__message site-reliability__message--error">{error}</p>;
    }

    if (isNotFound) {
      return (
        <p className="site-reliability__message">
          No reliability assessment: the site has no configuration.
        </p>
      );
    }

    return <p className="site-reliability__message">No data</p>;
  }
}
